<!-- app/javascript/dashboard/routes/dashboard/kanban/components/KanbanColumn.vue -->
<template>
  <section
    :class="[
      'kanban-col',
      isVirtual ? 'virtual' : 'real',
      'flex-none min-w-[280px] w-[320px] bg-gray-50 rounded-lg col-shadow flex flex-col overflow-hidden'
    ]"
  >
    <header
      class="kanban-header px-4 py-3 flex items-center justify-between rounded-t-lg"
      :style="[headerStyleFn(column), { pointerEvents: 'none' }]"
    >
      <!-- Título com edição inline -->
      <h2
        class="font-medium truncate flex-1 min-w-0"
        :style="{ color: titleColorFn(column) }"
      >
        <!-- MODO EDIÇÃO -->
        <template v-if="editingTitle">
          <input
            ref="titleInput"
            v-model.trim="draftTitle"
            class="w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm outline-none shadow-sm text-black"
            :style="{ pointerEvents: 'auto', color: '#000' }"
            maxlength="120"
            @keydown.enter.prevent="saveEditTitle"
            @keydown.esc.prevent="cancelEditTitle"
            @blur="saveEditTitle"
          />
        </template>

        <!-- MODO EXIBIÇÃO (duplo clique para editar) -->
        <template v-else>
          <span class="block truncate">
            <span
              class="inline cursor-text"
              :style="{ pointerEvents: 'auto' }"
              @dblclick.stop="startEditTitle"
            >
              {{ (column.title || column.name) }}
            </span>
          </span>
        </template>
      </h2>

      <div class="flex items-center gap-2">
        <span
          class="text-xs opacity-80"
          :style="{ color: titleColorFn(column), pointerEvents: 'auto' }"
        >
          {{ (column.cards || []).length }}
        </span>

        <template v-if="!isVirtual">
          <input
            class="w-6 h-6 p-0 border rounded cursor-pointer"
            type="color"
            :value="column.color || '#dbeafe'"
            @input="e => onColorInputFn(column, e.target.value)"
            @change="e => onColorInputFn(column, e.target.value)"
            :style="{ pointerEvents: 'auto' }"
          />
          <button
            class="col-handle opacity-80 hover:opacity-100 cursor-grab select-none"
            :style="{ pointerEvents: 'auto' }"
          >
            ⠿
          </button>
        </template>
      </div>
    </header>

    <!-- Lista de cards (draggable interno) -->
    <draggable
      :ref="el => registerCardsListFn(el, column)"
      v-model="column.cards"
      item-key="id"
      :group="cardsDndGroupFn(column)"
      :sort="!isVirtual"
      :clone="cloneCardFn"
      :animation="180"
      :draggable="'.kanban-card'"
      :ghost-class="'card-ghost'"
      :chosen-class="'card-chosen'"
      :drag-class="'card-drag'"
      :fallback-on-body="true"
      :force-fallback="true"
      :fallback-tolerance="8"
      :empty-insert-threshold="9999"
      :swap-threshold="0.2"
      :invert-swap="true"
      :inverted-swap-threshold="1"
      :dragover-bubble="true"
      :direction="'vertical'"
      :scroll-sensitivity="60"
      :scroll-speed="18"
      :data-col-id="column.id"
      @start="onDragStartFn"
      @move="evt => onCardMoveFn(evt, column)"
      @change="evt => onCardChangeFn(evt, column)"
      @end="onDragEndFn"
      class="cards-scroll flex-1 p-3 space-y-3 min-h-[300px] overflow-y-auto pr-1 pb-6"
      :style="cardsBgStyleFn(column)"
    >
      <template #item="{ element: card, index }">
        <!-- IMPORTANTE: o filho DIRETO do draggable precisa ter .kanban-card -->
        <div
          class="kanban-card"
          v-show="cardVisibleFn(card)"
          @contextmenu.prevent="openCardMenu($event, card)"
        >
          <div v-if="isDropAtFn(column.id, index)" class="drop-line" />
          <KanbanCard
            :card="card"
            :col="column"
            :index="index"
            :isDropAtFn="isDropAtFn"
            :onCardClickFn="onCardClickFn"
            :formatMsgTimeFn="formatMsgTimeFn"
            :openImagePreviewFn="openImagePreviewFn"
          />
        </div>
      </template>

      <template #footer>
        <div
          v-if="isDropAtFn(column.id, (column.cards?.length || 0))"
          class="drop-line"
        />
        <div
          v-if="(column.cards || []).length === 0 && !isDropAtFn(column.id, 0)"
          class="text-gray-400 text-sm italic"
        >
          (sem cards)
        </div>
        <div class="drop-filler h-12"></div>
      </template>
    </draggable>

    <!-- === MENU DE CONTEXTO (teleport para body, z-index máximo) === -->
    <teleport to="body">
      <div
        v-if="ctxMenu.open"
        ref="ctxMenuEl"
        :style="{
          position: 'fixed',
          top: ctxMenu.y + 'px',
          left: ctxMenu.x + 'px',
          zIndex: 2147483647
        }"
        class="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-md shadow-lg select-none text-gray-800 dark:text-gray-100"
        @contextmenu.prevent
      >
        <button
          class="block w-full text-left px-3 py-2 text-sm font-medium
                text-gray-900 hover:text-white hover:bg-gray-700
                dark:text-white dark:hover:text-white dark:hover:bg-slate-700
                transition-colors focus:outline-none"
          @click.stop="deleteCardFromKanban"
        >
          Remover do Kanban
        </button>
      </div>
    </teleport>

    <!-- === MODAL DE CONFIRMAÇÃO (teleport para body, z-index máximo) === -->
    <teleport to="body">
      <div v-if="confirmDelete.open" class="fixed inset-0" :style="{ zIndex: 2147483647 }">
        <div class="absolute inset-0 bg-black/40 backdrop-blur-[1px]" @click="closeConfirmDelete()"></div>
        <div
          class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(92vw,420px)] bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-xl"
        >
          <div class="px-4 pt-4 pb-2">
            <h3 class="text-base font-semibold">Remover do Kanban</h3>
            <p class="mt-2 text-sm text-gray-600 dark:text-gray-300">
              Tem certeza que deseja remover o card
              <span class="font-medium">
                “{{ (confirmDelete.card?.title || confirmDelete.card?.name || 'sem título') }}”
              </span>
              deste Kanban? A conversa no Chatwoot <span class="font-semibold">não</span> será excluída.
            </p>
          </div>
          <div class="px-4 py-3 flex items-center justify-end gap-2 bg-gray-50 dark:bg-slate-900/40 rounded-b-lg">
            <button
              class="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-slate-600 hover:bg-gray-100 dark:hover:bg-slate-800"
              :disabled="confirmDelete.busy"
              @click="closeConfirmDelete()"
            >
              Cancelar
            </button>
            <button
              class="px-3 py-1.5 text-sm rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
              :disabled="confirmDelete.busy"
              @click="performDeleteCard"
            >
              <span v-if="confirmDelete.busy">Removendo…</span>
              <span v-else>Remover</span>
            </button>
          </div>
        </div>
      </div>
    </teleport>
  </section>
</template>

<script>
import draggable from 'vuedraggable';
import KanbanCard from './KanbanCard.vue';

import kanbanAPI from 'dashboard/api/kanban';

export default {
  name: 'KanbanColumn',
  components: { draggable, KanbanCard },
  props: {
    column: { type: Object, required: true },
    isVirtual: { type: Boolean, required: true },

    // helpers/handlers do pai
    headerStyleFn: { type: Function, required: true },
    titleColorFn: { type: Function, required: true },
    cardsBgStyleFn: { type: Function, required: true },
    onColorInputFn: { type: Function, required: true },

    cardsDndGroupFn: { type: Function, required: true },
    cloneCardFn: { type: Function, required: true },
    registerCardsListFn: { type: Function, required: true },

    onDragStartFn: { type: Function, required: true },
    onCardMoveFn: { type: Function, required: true },
    onCardChangeFn: { type: Function, required: true },
    onDragEndFn: { type: Function, required: true },

    isDropAtFn: { type: Function, required: true },
    onCardClickFn: { type: Function, required: true },
    cardVisibleFn: { type: Function, required: true },
    formatMsgTimeFn: { type: Function, required: true },
    openImagePreviewFn: { type: Function, required: true },
  },

  data() {
    return {
      // edição de título
      editingTitle: false,
      draftTitle: '',
      savingTitle: false,

      // menu de contexto
      ctxMenu: {
        open: false,
        x: 0,
        y: 0,
        card: null,
      },

      // confirmação de remoção
      confirmDelete: {
        open: false,
        card: null,
        busy: false,
      },

      // ==== NOVO: identificador do componente p/ fechar menus globais ====
      _ctxOwnerId: Math.random().toString(36).slice(2),
    };
  },

  mounted() {
    // Ouve quando QUALQUER coluna abrir menu e fecha o próprio se não for o "dono"
    window.addEventListener('kanban:ctx:open', this._onGlobalCtxOpen);

    // ==== NOVO: fecha o menu em QUALQUER clique/touch/drag fora (captura) ====
    document.addEventListener('pointerdown', this._onAnyPointerDown, true);
    document.addEventListener('wheel', this._onAnyWheel, { capture: true, passive: true });
    document.addEventListener('dragstart', this._onAnyDragStart, true);
  },

  beforeUnmount() {
    try {
      window.removeEventListener('kanban:ctx:open', this._onGlobalCtxOpen);
    } catch (_) {}

    // limpeza dos listeners globais de captura
    try { document.removeEventListener('pointerdown', this._onAnyPointerDown, true); } catch (_) {}
    try { document.removeEventListener('wheel', this._onAnyWheel, { capture: true }); } catch (_) {}
    try { document.removeEventListener('dragstart', this._onAnyDragStart, true); } catch (_) {}
  },

  methods: {
    // ===== Edição inline do título =====
    startEditTitle() {
      if (this.savingTitle || this.isVirtual) return;
      const current = (this.column.title || this.column.name || '').trim();
      this.draftTitle = current;
      this.editingTitle = true;
      this.$nextTick(() => {
        const el = this.$refs.titleInput;
        if (el && el.focus) {
          el.focus();
          try { el.select(); } catch (_) {}
        }
      });
    },

    cancelEditTitle() {
      this.editingTitle = false;
      this.draftTitle = '';
      this.savingTitle = false;
    },

    async saveEditTitle() {
      if (this.savingTitle) return;

      const newTitle = (this.draftTitle || '').trim();
      const current = (this.column.title || this.column.name || '').trim();

      if (!newTitle || newTitle === current) {
        this.cancelEditTitle();
        return;
      }

      this.savingTitle = true;
      const old = { title: this.column.title, name: this.column.name };

      // otimista
      if (this.$set) {
        this.$set(this.column, 'title', newTitle);
        this.$set(this.column, 'name', newTitle);
      } else {
        this.column.title = newTitle;
        this.column.name = newTitle;
      }

      try {
        const { data } = await kanbanAPI.updateColumn(this.column.id, {
          title: newTitle,
        });

        const serverTitle = (data && (data.title || data.name)) ? (data.title || data.name) : null;
        if (serverTitle) {
          if (this.$set) {
            this.$set(this.column, 'title', serverTitle);
            this.$set(this.column, 'name', serverTitle);
          } else {
            this.column.title = serverTitle;
            this.column.name = serverTitle;
          }
        }

        this.cancelEditTitle();
      } catch {
        // rollback
        if (this.$set) {
          this.$set(this.column, 'title', old.title);
          this.$set(this.column, 'name', old.name);
        } else {
          this.column.title = old.title;
          this.column.name = old.name;
        }
        this.cancelEditTitle();
        try {
          this.$store?.dispatch?.('notifications/show', {
            message: 'Falha ao salvar título da coluna.',
            type: 'error',
          });
        } catch (_) {}
      } finally {
        this.savingTitle = false;
      }
    },

    // ===== Context menu (clique direito) =====
    openCardMenu(evt, card) {
      try { evt.stopPropagation(); } catch (_) {}

      // emite evento global avisando que um menu foi aberto
      try {
        window.dispatchEvent(new CustomEvent('kanban:ctx:open', { detail: { id: this._ctxOwnerId } }));
      } catch (_) {}

      this.ctxMenu.open = true;
      this.ctxMenu.x = evt.clientX;
      this.ctxMenu.y = evt.clientY;
      this.ctxMenu.card = card;

      // listeners para fechar (em bolha, não captura)
      this.$nextTick(() => {
        window.addEventListener('click', this._closeCtxByClick, { once: true });
        window.addEventListener('contextmenu', this._closeCtxByClick, { once: true });
        window.addEventListener('keydown', this._closeCtxByEsc, { once: true });
      });
    },

    // fecha este menu quando outro componente abrir o dele
    _onGlobalCtxOpen(e) {
      const otherId = e && e.detail && e.detail.id;
      if (!otherId || otherId === this._ctxOwnerId) return;
      this.closeCardMenu();
    },

    _closeCtxByClick() {
      this.closeCardMenu();
    },
    _closeCtxByEsc(e) {
      if (e.key === 'Escape') this.closeCardMenu();
    },

    closeCardMenu() {
      this.ctxMenu.open = false;
      this.ctxMenu.card = null;
      try {
        window.removeEventListener('click', this._closeCtxByClick);
        window.removeEventListener('contextmenu', this._closeCtxByClick);
        window.removeEventListener('keydown', this._closeCtxByEsc);
      } catch (_) {}
    },

    // ==== NOVO: fechar em qualquer pointerdown fora do menu (captura) ====
    _onAnyPointerDown(e) {
      if (!this.ctxMenu.open) return;
      const el = this.$refs.ctxMenuEl;
      const t = e?.target;
      if (!el) { this.closeCardMenu(); return; }
      // se o clique NÃO foi dentro do menu, fecha
      if (t && !el.contains(t)) {
        this.closeCardMenu();
      }
    },
    _onAnyWheel() {
      if (this.ctxMenu.open) this.closeCardMenu();
    },
    _onAnyDragStart() {
      if (this.ctxMenu.open) this.closeCardMenu();
    },

    // Abre confirmação
    deleteCardFromKanban() {
      const c = this.ctxMenu.card;
      this.closeCardMenu();
      if (!c || !c.id) return;
      this.confirmDelete.open = true;
      this.confirmDelete.card = c;
      this.confirmDelete.busy = false;
    },

    // Agora aceita "force" para ignorar busy
    closeConfirmDelete(force = false) {
      if (this.confirmDelete.busy && !force) return;
      this.confirmDelete.open = false;
      this.confirmDelete.card = null;
      this.confirmDelete.busy = false;
    },

    async performDeleteCard() {
      const c = this.confirmDelete.card;
      if (!c || !c.id || this.confirmDelete.busy) return;

      this.confirmDelete.busy = true;

      // Remoção otimista
      const oldCards = Array.isArray(this.column.cards) ? [...this.column.cards] : [];
      const idx = this.column.cards.findIndex(k => k.id === c.id);
      if (idx >= 0) this.column.cards.splice(idx, 1);

      try {
        await kanbanAPI.deleteCard(c.id);
        // sucesso: libera busy e fecha forçado
        this.confirmDelete.busy = false;
        this.closeConfirmDelete(true);
      } catch {
        // rollback
        this.$set ? this.$set(this, 'column', { ...this.column, cards: oldCards }) : (this.column.cards = oldCards);
        try {
          this.$store?.dispatch?.('notifications/show', {
            message: 'Falha ao remover card.',
            type: 'error',
          });
        } catch (_) {}
        this.confirmDelete.busy = false;
      }
    },
  },
};
</script>
